/* Warehouse Bold — product lower page: tab scrollspy, read-more clamp,
   recommendation tabs, mobile view-more. */
(() => {
  const init = () => {
    const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---------------- Read more ---------------- */
    /* The control only exists if the copy actually overflows the clamp —
       a short description must not get a link that does nothing. */
    const clamp = document.querySelector('[data-wb-clamp]');
    const readmore = document.querySelector('[data-wb-readmore]');

    /* Round the clamp DOWN to a line boundary. The CSS height is a target; left
       raw it cuts through the middle of whatever line it lands on (Owen's
       screenshot, 2026-08-16 — at 190px the cut fell inside a line box running
       177→196, slicing the glyphs in half).
       ⚠ Measured from the TEXT, not from the box: this is merchant rich text,
       two or more paragraphs with margins between them, so the line grid is not
       a constant multiple of line-height and cannot be computed. Range rects
       give the real line boxes wherever they happen to fall.
       ⚠ The result goes into a custom property, never an inline max-height, so
       `.wb-clamp.is-open { max-height: none }` still wins when the shopper
       opens it. */
    const snapClamp = () => {
      if (!clamp || clamp.classList.contains('is-open')) return;
      // Read the target fresh each time, so repeated runs never compound.
      clamp.style.removeProperty('--wb-clamp-h');
      const target = parseFloat(getComputedStyle(clamp).maxHeight);
      if (!isFinite(target)) return;
      const top = clamp.getBoundingClientRect().top;
      const range = document.createRange();
      const lines = [];
      /* ⚠ Walk EVERY text node, not the depth-1 children of a block whitelist.
         Merchant copy routinely puts a whole line inside an inline element —
         `<p><b>Features:</b></p>`, `<h3><a>Manual</a></h3>`, and worst of all
         `<b>To Use:</b><br>text`, which hides a line in the MIDDLE of a block
         the whitelist otherwise measures. Lines it cannot see cannot be
         protected: lastFit and firstCut straddle the invisible line and the
         "safe" midpoint lands inside its glyphs — the original bug, rebuilt by
         the fix meant to remove it.
         Measured on zip-chem-cor-ban-35 with the whitelist version: 18 of 21
         lines seen, and the cut sliced at 4 of 5 targets — at 190px it cut 145px
         straight through a line running 135.7→154.7.
         It also rescues descriptions that arrive as a bare text node with no
         block wrapper, where the whitelist matched nothing and the snap silently
         gave up. */
      const walk = document.createTreeWalker(clamp, NodeFilter.SHOW_TEXT);
      for (let node = walk.nextNode(); node; node = walk.nextNode()) {
        if (!node.nodeValue.trim()) continue; // whitespace between blocks has no line box
        range.selectNodeContents(node);
        for (const r of range.getClientRects()) {
          if (r.height > 0) lines.push({ top: r.top - top, bottom: r.bottom - top });
        }
      }
      /* Replaced content occupies rows too and has no text node to select. */
      clamp.querySelectorAll('img, table, hr, iframe, svg, video, canvas').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height > 0) lines.push({ top: r.top - top, bottom: r.bottom - top });
      });
      if (!lines.length) return;
      lines.sort((a, b) => a.top - b.top);
      let lastFit = null;
      let firstCut = null;
      for (const l of lines) {
        if (l.bottom <= target) lastFit = l;
        // Sorted by top, so the first row that does not fit is the one the cut
        // would touch. Breaking also stops lastFit being reassigned past it.
        else { firstCut = l; break; }
      }
      if (!lastFit || !firstCut) return;                          // nothing to trim
      // Cut in the leading BETWEEN the two lines, so neither is touched.
      const snapped = Math.floor((lastFit.bottom + firstCut.top) / 2);
      if (snapped > 0 && snapped < target) clamp.style.setProperty('--wb-clamp-h', snapped + 'px');
    };

    if (clamp && readmore) {
      snapClamp();
      const overflows = clamp.scrollHeight > clamp.clientHeight + 4;
      readmore.hidden = !overflows;
      /* Re-snap whenever the lines can rewrap. ⚠ Debounced with a timer rather
         than rAF: rAF never fires in a hidden document, and a tab restored from
         the background would keep a stale cut. */
      let snapT = null;
      const resnap = () => { clearTimeout(snapT); snapT = setTimeout(snapClamp, 120); };
      window.addEventListener('resize', resnap);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(snapClamp);
      readmore.addEventListener('click', () => {
        const open = clamp.classList.toggle('is-open');
        readmore.textContent = readmore.dataset.less && open ? readmore.dataset.less : readmore.textContent;
        readmore.hidden = open;
      });
    }

    /* ---------------- Tab scrollspy ---------------- */
    const bar = document.querySelector('[data-wb-tabs]');
    const tabbar = document.querySelector('[data-wb-tabbar]');
    if (bar && tabbar) {
      const links = [...tabbar.querySelectorAll('a')];
      // Ids come from the rendered tabs, so the index can never drift out
      // of step with the pills (sections vanish per product).
      const ids = links.map((a) => a.getAttribute('href').slice(1));
      let lock = null, lockT = null;
      const release = () => { if (lock !== null) { lock = null; clearTimeout(lockT); spy(); } };

      const spy = () => {
        // Self-disable rather than animating a hidden element.
        if (getComputedStyle(bar).display === 'none') return;
        const off = bar.getBoundingClientRect().bottom + 12;
        let cur = 0;
        if (lock !== null) {
          cur = lock; // navigating: hold the clicked tab, ignore passing sections
        } else {
          ids.forEach((id, n) => {
            const el = document.getElementById(id);
            if (el && el.getBoundingClientRect().top <= off) cur = n;
          });
          // Past the last section nothing is current, so no pill stays lit.
          const sec = document.getElementById(ids[cur]);
          if (sec && sec.getBoundingClientRect().bottom <= off) cur = -1;
        }
        let changed = false;
        links.forEach((a, n) => {
          const on = n === cur;
          if (on !== a.classList.contains('is-active')) changed = true;
          a.classList.toggle('is-active', on);
        });
        if (changed) {
          const act = tabbar.querySelector('a.is-active');
          if (act && tabbar.scrollWidth > tabbar.clientWidth) {
            const left = act.offsetLeft - (tabbar.clientWidth - act.offsetWidth) / 2;
            tabbar.scrollTo({ left: Math.max(0, left), behavior: reduced() ? 'auto' : 'smooth' });
          }
        }
      };

      /* Target lock: clicking a tab paints it active immediately and pins
         the spy for the duration of the smooth scroll, or every section
         passed on the way lights up in turn. Released on arrival, on any
         user input, and by a safety timeout for browsers with no
         scrollend. */
      tabbar.addEventListener('click', (e) => {
        const a = e.target.closest('a[href^="#"]');
        if (!a) return;
        lock = links.indexOf(a);
        spy();
        clearTimeout(lockT);
        lockT = setTimeout(release, 1500);
      });
      if ('onscrollend' in window) addEventListener('scrollend', release);
      ['wheel', 'touchstart'].forEach((ev) => addEventListener(ev, () => { lock = null; clearTimeout(lockT); }, { passive: true }));
      addEventListener('scroll', spy, { passive: true });
      addEventListener('resize', spy, { passive: true });
      spy();
    }

    /* ---------------- Recommendation tabs ---------------- */
    const recTabs = document.querySelector('[data-wb-rec-tabs]');
    if (recTabs) {
      recTabs.addEventListener('click', (e) => {
        const b = e.target.closest('[data-wb-rec]');
        if (!b || recTabs.classList.contains('wb-rec__tabs--single')) return;
        const key = b.dataset.wbRec;
        recTabs.querySelectorAll('[data-wb-rec]').forEach((t) => t.classList.toggle('is-active', t === b));
        document.querySelectorAll('[data-wb-rec-panel]').forEach((pnl) => { pnl.hidden = pnl.dataset.wbRecPanel !== key; });
        capMobile();
      });
    }

    /* Mobile: two rows (4 cards) at rest, SHOW ALL reveals the rest. The
       desktop carousel dots are gone — under a static 2-col grid they
       promised a swipe with nothing to swipe. */
    const moreBtn = document.querySelector('[data-wb-rec-more]');
    const capMobile = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const panel = [...document.querySelectorAll('[data-wb-rec-panel]')].find((pl) => !pl.hidden);
      if (!panel || !moreBtn) return;
      const cards = panel.querySelectorAll('.wb-card').length;
      /* ⚠ `wbExpanded` is why this is not idempotent-by-accident. capMobile
         re-applies the cap every time it runs, and it runs on `resize` — and
         on iOS Safari SCROLLING fires resize, because collapsing the address
         bar changes the viewport height. So expanding the grid and then
         scrolling re-capped it and the revealed products vanished under the
         reader (Owen, 2026-08-14). Nothing recorded that a person had asked
         for them.
         The flag lives on the PANEL, not in a closure variable: with both
         recommendation tabs present, one shared flag would carry an expansion
         from one list over to the other. */
      if (!mobile || cards <= 4 || panel.dataset.wbExpanded === '1') {
        panel.classList.remove('is-capped');
        moreBtn.hidden = true;
        return;
      }
      panel.classList.add('is-capped');
      moreBtn.hidden = false;
      moreBtn.textContent = moreBtn.dataset.label
        ? moreBtn.dataset.label.replace('@@N@@', cards)
        : moreBtn.textContent.replace(/\d+/, cards);
    };
    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        const panel = [...document.querySelectorAll('[data-wb-rec-panel]')].find((pl) => !pl.hidden);
        if (panel) {
          panel.dataset.wbExpanded = '1';
          panel.classList.remove('is-capped');
        }
        moreBtn.hidden = true;
      });
      capMobile();
      addEventListener('resize', capMobile, { passive: true });
    }

    /* ---------------- Shop the range: row scrollers ---------------- */
    /* Arrows are desktop-only in CSS, but the [hidden] bookkeeping runs
       everywhere — it is cheap, and it means a window resize past 990 finds
       the state already correct rather than showing two live arrows on a row
       that cannot scroll. */
    document.querySelectorAll('[data-wb-range-track]').forEach((track) => {
      /* Scope to the ROW. The arrows have lived both inside .wb-range__scrollwrap
         and up in the heading; scoping one level above covers either without
         this lookup silently bailing and leaving both buttons on the `hidden`
         they were server-rendered with — a row that scrolls 4000px with no
         way to drive it. */
      const row = track.closest('.wb-range__row');
      if (!row) return;
      const prev = row.querySelector('[data-wb-range-prev]');
      const next = row.querySelector('[data-wb-range-next]');
      const bar = row.querySelector('[data-wb-range-prog]');
      const thumb = bar && bar.querySelector('span');
      if (!prev || !next) return;
      /* ⚠ Derived from where the CARDS are, never from scrollLeft against 0
         and scrollWidth. The track carries 8px of padding (paid back as a
         negative margin, so focus rings are not sheared by the scrollport),
         and `scroll-snap-align: start` snaps the first card to the inside of
         that padding — so a row sitting untouched at its start rests at
         scrollLeft 8, not 0, and a threshold of `<= 2` left a live "previous"
         arrow on every row at rest. Card geometry has no such offset and is
         also immune to sub-pixel widths. */
      const sync = () => {
        const cards = track.querySelectorAll('.wb-card');
        if (!cards.length) { prev.hidden = true; next.hidden = true; return; }
        const port = track.getBoundingClientRect();
        prev.hidden = cards[0].getBoundingClientRect().left >= port.left - 1;
        next.hidden = cards[cards.length - 1].getBoundingClientRect().right <= port.right + 1;
        /* Scroll position bar — the cue where there is no pointer to reveal
           an arrow. Percentages, not pixels, so it needs no recalculation on
           resize beyond what the scroll/resize listeners already do. */
        if (bar && thumb) {
          const ratio = track.clientWidth / track.scrollWidth;
          if (ratio >= 1) {
            bar.hidden = true;
          } else {
            bar.hidden = false;
            thumb.style.width = (ratio * 100) + '%';
            /* Clamped: iOS rubber-banding drives scrollLeft negative and past
               the end, which would otherwise push the thumb outside its
               track and make the row look broken at exactly the moment
               someone is discovering it scrolls. */
            const max = track.scrollWidth - track.clientWidth;
            const at = Math.min(Math.max(track.scrollLeft, 0), max);
            thumb.style.left = ((at / track.scrollWidth) * 100) + '%';
          }
        }
      };
      /* One card plus its gap short of a full width, so the card at the fold
         stays on screen as the anchor for where you just were. */
      const step = () => Math.max(160, track.clientWidth - 180);
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });
      addEventListener('resize', sync, { passive: true });
      sync();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
